import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { Button } from '@material-ui/core';
import Divider from '@material-ui/core/Divider';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Grid from '@material-ui/core/Grid';
import RadioButton from '@material-ui/core/Radio';
import Typography from '@material-ui/core/Typography';
import strings from 'strings';
import FPIcon from 'UI/components/atoms/FPIcon';
import TextBox from 'UI/components/atoms/TextBox';
import AutocompleteSelect from 'UI/components/molecules/AutocompleteSelect';
import { FORM_SPACING, gridDimensions } from 'UI/constants/dimensions';
import { SvgDeleteFilled } from 'UI/res';
import {
  countryCodeOptionLabel,
  EXT_PHONE_VALIDATION,
  idOptionSelected,
  PHONE_VALIDATION,
  REQUIRED_VALIDATION,
  titleOptionLabel
} from 'UI/utils';
import { buildBasePropsForField } from 'UI/utils/forms';
import { Selectors } from 'UI/utils/renderers';

import { useStyles } from '../styles';
import { PHONE_ROW_LABELS, PHONE_ROW_PREFIX, PHONE_USE_MAP } from '../utils';

/**
 * @typedef {Object} PhoneRow
 * @property {number} id
 * @property {true} isDefault
 * @property {string | number} phone
 * @property {number} ext
 * @property {*} use
 * @property {*} type
 * @property {number} [phoneId]
 */

const USE_OPTIONS = Object.values(PHONE_USE_MAP);

/**
 * @param {boolean} isEditing
 * @param {number} mdSize
 */
const getColumnSize = (isEditing, mdSize) => {
  const config = isEditing
    ? gridDimensions.editionDrawer
    : { ...gridDimensions.itemCreation, md: mdSize };
  return config;
};

/**
 * @param {string} label
 * @param {boolean} required
 */
const getFieldLabel = (label, required) => `${label} ${required ? '*' : ''}`;

/**
 * @param {Object} props
 * @param {Object} props.catalogues
 * @param {PhoneRow} props.item
 * @param {Function} props.onRemovePhoneRow
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.isEditing]
 * @param {boolean} [props.isRequired]
 * @param {number} [props.index]
 * @param {Function} [props.onAfterChange] - Called after any field change so AutocompleteSelect-based fields (Code / Use / Type) trigger autosave.
 * @param {number} [props.rowsCount]
 */
const PhoneFieldsRow = ({
  catalogues,
  disabled,
  index,
  isEditing,
  isRequired,
  item,
  onAfterChange,
  onRemovePhoneRow,
  rowsCount
}) => {
  const classes = useStyles();
  const { register, unregister, errors, setValue, watch, getValues } = useFormContext();
  const formValues = watch();

  const handleSelectChange = (name, value) => {
    setValue(name, value, true);
    onAfterChange && onAfterChange();
  };

  const handleTextChange = (name, value) => {
    setValue(name, value, true);
  };

  useEffect(() => {
    const shouldValidate = isRequired ? REQUIRED_VALIDATION : { required: false };
    register({ name: `${PHONE_ROW_PREFIX.CountryCode}${item.id}` }, shouldValidate);
    register(
      { name: `${PHONE_ROW_PREFIX.Phone}${item.id}` },
      { ...shouldValidate, ...PHONE_VALIDATION }
    );
    register({ name: `${PHONE_ROW_PREFIX.Ext}${item.id}` }, EXT_PHONE_VALIDATION);

    setValue(`${PHONE_ROW_PREFIX.CountryCode}${item.id}`, item.countryCode);
    setValue(`${PHONE_ROW_PREFIX.Phone}${item.id}`, item.phone);
    setValue(`${PHONE_ROW_PREFIX.Ext}${item.id}`, item.ext);

    return () => {
      unregister(`${PHONE_ROW_PREFIX.CountryCode}${item.id}`);
      unregister(`${PHONE_ROW_PREFIX.Phone}${item.id}`);
      unregister(`${PHONE_ROW_PREFIX.Ext}${item.id}`);
    };
  }, [isRequired, item.countryCode, item.ext, item.id, item.phone, register, setValue, unregister]);

  const phoneFieldValue = getValues(`${PHONE_ROW_PREFIX.Phone}${item.id}`);
  const isPhoneFieldNotEmpty = phoneFieldValue !== '';

  useEffect(() => {
    if ((!isRequired && isPhoneFieldNotEmpty) || isRequired) {
      register({ name: `${PHONE_ROW_PREFIX.Use}${item.id}` }, REQUIRED_VALIDATION);
      register({ name: `${PHONE_ROW_PREFIX.Type}${item.id}` }, REQUIRED_VALIDATION);
    } else {
      register({ name: `${PHONE_ROW_PREFIX.Use}${item.id}` }, { required: false });
      register({ name: `${PHONE_ROW_PREFIX.Type}${item.id}` }, { required: false });
    }

    setValue(`${PHONE_ROW_PREFIX.Use}${item.id}`, item.use);
    setValue(`${PHONE_ROW_PREFIX.Type}${item.id}`, item.type);

    return () => {
      unregister(`${PHONE_ROW_PREFIX.Use}${item.id}`);
      unregister(`${PHONE_ROW_PREFIX.Type}${item.id}`);
    };
  }, [
    isPhoneFieldNotEmpty,
    isRequired,
    item.id,
    item.type,
    item.use,
    register,
    setValue,
    unregister
  ]);

  const shouldDisplayRemoveAction = isEditing ? !item.isDefault && !disabled : index;

  const inputType = item.phone.length <= PHONE_VALIDATION.maxLength.value ? 'phone' : 'text';

  const defaultPhoneColumnValues = getColumnSize(isEditing, 3);
  const { sm: defaultSm, xs: defaultXs, md: defaultMd, lg: defaultLg } = defaultPhoneColumnValues;

  const smallPhoneColumnValues = getColumnSize(isEditing, 2);
  const { sm: smallSm, xs: smallXs, md: smallMd, lg: smallLg } = smallPhoneColumnValues;

  return (
    <>
      <Grid container spacing={FORM_SPACING}>
        <Grid item xs={defaultSm} sm={defaultXs} md={defaultMd} lg={defaultLg}>
          <AutocompleteSelect
            {...buildBasePropsForField(`${PHONE_ROW_PREFIX.CountryCode}${item.id}`, errors)}
            placeholder={getFieldLabel(PHONE_ROW_LABELS.CountryCode, isRequired)}
            defaultOptions={catalogues?.countryCodes}
            onSelect={handleSelectChange}
            selectedValue={formValues[`${PHONE_ROW_PREFIX.CountryCode}${item.id}`] || null}
            renderOption={countryCodeOptionLabel}
            getOptionLabel={countryCodeOptionLabel}
            getOptionSelected={Selectors.byId}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={defaultSm} sm={defaultXs} md={defaultMd} lg={defaultLg}>
          <TextBox
            {...buildBasePropsForField(`${PHONE_ROW_PREFIX.Phone}${item.id}`, errors)}
            label={getFieldLabel(PHONE_ROW_LABELS.Phone, isRequired)}
            placeholder="(999)-999-9999"
            onChange={handleTextChange}
            value={formValues[`${PHONE_ROW_PREFIX.Phone}${item.id}`] || ''}
            inputType={inputType}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={smallSm} sm={smallXs} md={smallMd} lg={smallLg}>
          <TextBox
            {...buildBasePropsForField(`${PHONE_ROW_PREFIX.Ext}${item.id}`, errors)}
            label={PHONE_ROW_LABELS.Ext}
            placeholder="999"
            onChange={handleTextChange}
            value={formValues[`${PHONE_ROW_PREFIX.Ext}${item.id}`] || ''}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={smallSm} sm={smallXs} md={smallMd} lg={smallLg}>
          <AutocompleteSelect
            {...buildBasePropsForField(`${PHONE_ROW_PREFIX.Use}${item.id}`, errors)}
            onSelect={handleSelectChange}
            selectedValue={formValues[`${PHONE_ROW_PREFIX.Use}${item.id}`] || null}
            placeholder={getFieldLabel(PHONE_ROW_LABELS.Use, isRequired)}
            defaultOptions={USE_OPTIONS}
            renderOption={titleOptionLabel}
            getOptionSelected={idOptionSelected}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={smallSm} sm={smallXs} md={smallMd} lg={smallLg}>
          <AutocompleteSelect
            {...buildBasePropsForField(`${PHONE_ROW_PREFIX.Type}${item.id}`, errors)}
            placeholder={getFieldLabel(PHONE_ROW_LABELS.Type, isRequired)}
            defaultOptions={catalogues?.phoneTypes}
            onSelect={handleSelectChange}
            selectedValue={formValues[`${PHONE_ROW_PREFIX.Type}${item.id}`] || null}
            renderOption={titleOptionLabel}
            getOptionSelected={idOptionSelected}
            disabled={disabled}
          />
        </Grid>
      </Grid>
      <div className={classes.actionRowContainer}>
        {rowsCount >= 2 && (
          <FormControlLabel
            name={`${PHONE_ROW_PREFIX.IsDefault}${item.isDefault}`}
            value={item.id}
            control={<RadioButton />}
            label={strings.inventory.phoneNumbers.component.defaultRadio}
            className={classes.radioText}
            disabled={disabled || !isPhoneFieldNotEmpty}
          />
        )}
        {shouldDisplayRemoveAction ? (
          <Button
            color="primary"
            className={classes.deleteRowContainer}
            onClick={() => onRemovePhoneRow(item)}
          >
            <FPIcon component={SvgDeleteFilled} aria-label="Remove Phone" size={20} />
            <Typography className={classes.removeText}>
              {strings.shared.ui.keepRemoveButtonsCopies.confirmButtonText}
            </Typography>
          </Button>
        ) : null}
      </div>
      {isEditing && <Divider />}
    </>
  );
};

PhoneFieldsRow.defaultProps = {
  isEditing: false,
  rowsCount: null,
  index: null,
  onAfterChange: undefined,
  disabled: false,
  isRequired: false
};

export default PhoneFieldsRow;
